import type { RouteLocationNormalized, Router } from 'vue-router'

import { flushBehaviorEvents, trackEvent } from './trackEvent'
import { BehaviorEventName, BehaviorScreen } from './types'

/**
 * Screen views and dwell time, driven off the router (VH-2670).
 *
 * A `screen_viewed` event is emitted when a screen is *left*, not when it is
 * entered, because the interesting number is how long someone spent on it.
 * Leaving happens three ways: navigating elsewhere, hiding the tab, and
 * closing it — all three are handled here.
 *
 * ## The screen never comes from the URL
 *
 * The IVD routes look like this:
 *
 *     /provider/:provider_uuid/insurance/patient/:patient_uuid/plan/:plan_uuid
 *
 * so anything derived from `route.path` or `route.fullPath` carries patient
 * identifiers into a table designed to hold none. The only accepted source is
 * `meta.screen`, which is a value from the {@see BehaviorScreen} enum.
 *
 * **A route with no `meta.screen` emits nothing.** No fallback to
 * `route.name`, no fallback to the path. A gap in coverage is visible and
 * cheap to fix; a path fallback is a PHI incident that looks like it is
 * working. The server rejects unknown screens too, but this is the half that
 * stops the value being constructed in the first place.
 */

declare module 'vue-router' {
  interface RouteMeta {

    /**
     * Which screen this route is, for behavior instrumentation (VH-2670).
     *
     * Declared here so adding it to a route is checked and autocompleted
     * rather than being an untyped bag key. Omitting it is legal and means
     * "do not track this route" — see `screenOf()` for why that has to fail
     * closed rather than fall back to the path.
     */
    screen?: BehaviorScreen
  }
}

let currentScreen: BehaviorScreen | null = null
let currentProviderUuid: string | null = null
let enteredAt: number | null = null
let installed = false

function screenOf(route: RouteLocationNormalized): BehaviorScreen | null {
  const screen = route.meta?.screen

  if (typeof screen !== 'string')
    return null

  // Checked against the enum rather than trusted as a string: a route that
  // sets meta.screen to something invented would otherwise be emitted and
  // rejected server-side, and the client is where that should be caught.
  const isKnownScreen = (Object.values(BehaviorScreen) as string[]).includes(screen)

  return isKnownScreen ? (screen as BehaviorScreen) : null
}

/**
 * Emit the dwell for the screen we are on, then stop the clock.
 *
 * Clearing `enteredAt` is what makes this safe to call from several places:
 * a tab hidden and then closed should produce one event, not two.
 */
function emitCurrentScreen(): void {
  if (currentScreen === null || enteredAt === null)
    return

  trackEvent(BehaviorEventName.ScreenViewed, currentScreen, {
    durationMs: Math.max(0, Math.round(performance.now() - enteredAt)),
  })

  enteredAt = null
}

/**
 * Wire screen tracking into the router.
 *
 * `afterEach` rather than `beforeEach`: a navigation that is cancelled or
 * redirected by a guard should not end the dwell on a screen the user never
 * actually left.
 */
export function installScreenTracking(router: Router): void {
  if (installed)
    return

  installed = true

  router.afterEach((to, from, failure) => {
    // afterEach fires for aborted and cancelled navigations too, with the
    // failure as its third argument. Without this the docstring above was
    // simply untrue: a guard that aborts a navigation to another path would
    // still end the dwell on the screen the user is sitting on and start
    // timing a screen they never reached.
    if (failure)
      return

    // Path, not fullPath — a query-only change (a filter, a tab) is not
    // leaving the screen. Matches how plugins/axios.ts decides the same
    // question for request cancellation.
    if (to.path === from.path)
      return

    emitCurrentScreen()

    currentScreen = screenOf(to)
    enteredAt = currentScreen === null ? null : performance.now()

    // Kept for the ingest request's X-Provider-UUID header, not for the event
    // payload — the server derives provider_id itself and never reads this
    // from the body. See the note in trackEvent.ts.
    const providerUuid = to.params.provider_uuid

    currentProviderUuid = typeof providerUuid === 'string' ? providerUuid : null
  })

  if (typeof window === 'undefined')
    return

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // The last screen before a tab is closed is exactly the drop-off this
      // epic exists to measure, and it is the one most easily lost. The event
      // has to be *created* before the buffer is flushed, so flush explicitly
      // here rather than relying on trackEvent's own pagehide listener
      // happening to run second.
      emitCurrentScreen()
      flushBehaviorEvents()

      return
    }

    // Back on screen: restart the clock rather than resuming it. Counting
    // background time would mean a tab left open overnight reports a
    // fourteen-hour dwell and drags every median with it.
    if (currentScreen !== null)
      enteredAt = performance.now()
  })

  window.addEventListener('pagehide', () => {
    emitCurrentScreen()
    flushBehaviorEvents()
  })
}

/**
 * The screen the user is on, or null if it is not a tracked one.
 *
 * Exposed so funnel events carry the same screen as the view events do,
 * without every call site re-deriving it — and without the instrumentation
 * module importing the router, which would close a cycle (the router imports
 * installScreenTracking from here).
 *
 * Null means the same thing it means everywhere else in this module: do not
 * emit. A funnel step on an untracked screen is a coverage gap, not a reason
 * to invent a screen value.
 */
export function activeScreen(): BehaviorScreen | null {
  return currentScreen
}

/**
 * The provider whose pages the user is currently on, for the ingest request's
 * X-Provider-UUID header.
 *
 * A Vector admin has no single "current" provider server-side — which one they
 * are acting as comes from that header, and `plugins/axios.ts` sets it from
 * this same route param on every normal request. Telemetry deliberately does
 * not go through axios, so it has to carry the header itself; without it the
 * endpoint cannot resolve a provider for an admin and rejects the batch with
 * a 403.
 *
 * This is a hospital identifier, not patient data, and it is the same value
 * already present in the URL of every page it is read from.
 */
export function activeProviderUuid(): string | null {
  return currentProviderUuid
}

/**
 * Leave the screen just entered without recording a view of it (VH-2865).
 *
 * For a page that only redirects. iv-patient-detail fetches the patient's plans
 * behind a spinner and replaces itself with the top-priority plan, so the view
 * it would record is a fetch time. Since the lookup flow lands there after
 * every plans-list verify, that put a near-zero dwell on the screen's median,
 * and counted every redirect as a visit. Call it just before the redirect.
 */
export function discardScreenView(): void {
  enteredAt = null
}

/** Test seam: forget the current screen and allow a re-install. */
export function __resetScreenTrackingForTests(): void {
  currentScreen = null
  currentProviderUuid = null
  enteredAt = null
  installed = false
}
